import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTreatmentZones } from '../hooks/useTreatmentZones';
import { useGA4 } from '../hooks/useGA4';
import { ArrowRight } from './icons';
import { SlotMachineButton } from './SlotMachineButton';
import { getBestImageUrl } from '../utils/imageUrl';
import { getCurrentLocale } from '../services/api';

// Utility function for className merging
function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}

// Function to determine zone type based on title
const getZoneType = (title: string) => {
  const lowerTitle = title.toLowerCase();
  // Detect NL/EN/AR keywords for each zone type
  if (
    lowerTitle.includes('hoofdhuid') ||
    lowerTitle.includes('scalp') ||
    lowerTitle.includes('hair') ||
    lowerTitle.includes('فروة') ||
    lowerTitle.includes('الرأس') ||
    lowerTitle.includes('شعر')
  ) {
    return 'hoofdhuid';
  } else if (
    lowerTitle.includes('snor') ||
    lowerTitle.includes('mustache') ||
    lowerTitle.includes('شارب')
  ) {
    return 'snor';
  } else if (
    lowerTitle.includes('baard') ||
    lowerTitle.includes('beard') ||
    lowerTitle.includes('لحية') ||
    lowerTitle.includes('اللحية') ||
    lowerTitle.includes('ذقن')
  ) {
    return 'baard';
  } else if (
    lowerTitle.includes('wenkbrauw') ||
    lowerTitle.includes('brow') ||
    lowerTitle.includes('حاجب') ||
    lowerTitle.includes('الحواجب')
  ) {
    return 'wenkbrauwen';
  }
  return 'hoofdhuid'; // Default fallback
};

// Anchor navigation component
const TreatmentZoneAnchors: React.FC<{ 
  activeZone: string; 
  onZoneChange: (zone: string) => void;
  headerHeight: number;
  treatmentZones?: any[] | null;
}> = ({ activeZone, onZoneChange, headerHeight, treatmentZones }) => {
  const { trackEvent } = useGA4();
  // Create zones array based on actual treatment zones from Strapi
  const zones = React.useMemo(() => {
    if (!treatmentZones || treatmentZones.length === 0) {
      return [];
    }

    // Filter out duplicates and create buttons for unique zones
    const seenTitles = new Set();
    const uniqueZones = treatmentZones.filter((zone) => {
      const title = zone.name;
      if (seenTitles.has(title)) {
        return false;
      }
      seenTitles.add(title);
      return true;
    });

    // Sort by order field (same as main treatment zones display)
    const sortedZones = uniqueZones.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    // Debug log to verify anchor ordering
    console.log('🔗 TreatmentZoneAnchors - Sorted order:', sortedZones.map(z => `${z.name}(${z.order})`).join(', '));

    return sortedZones.map((zone) => ({
      id: String(zone.id),
      label: zone.name,
      zoneType: getZoneType(zone.name)
    }));
  }, [treatmentZones]);

  const handleZoneClick = (zoneId: string) => {
    // Track treatment zone navigation
    const zone = zones.find(z => z.id === zoneId);
    trackEvent('treatment_zone_click', {
      zone_id: zoneId,
      zone_label: zone?.label || 'unknown',
      zone_type: zone?.zoneType || 'unknown'
    });
    
    // Immediately set as active zone
    console.log('Clicking zone:', zoneId, 'setting as active immediately');
    onZoneChange(zoneId);
    
    // Scroll to the zone section
    const zoneSection = document.getElementById(`zone-${zoneId}`);
    if (zoneSection) {
      const headerHeight = document.querySelector('header')?.offsetHeight || 80;
      const elementPosition = zoneSection.offsetTop - headerHeight - 60; // More offset for better positioning
      
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        window.scrollTo({
          top: Math.max(0, elementPosition), // Ensure we don't scroll above the page
          behavior: 'smooth'
        });
        
        // After scroll, ensure the zone is still active
        setTimeout(() => {
          console.log('Post-scroll check for zone:', zoneId);
          onZoneChange(zoneId);
        }, 500);
      });
    } else {
      // Fallback: scroll to the treatment zones section
      const treatmentSection = document.querySelector('.treatment-zones-section');
      if (treatmentSection) {
        const headerHeight = document.querySelector('header')?.offsetHeight || 80;
        const elementPosition = (treatmentSection as HTMLElement).offsetTop - headerHeight - 60;
        
        requestAnimationFrame(() => {
          window.scrollTo({
            top: Math.max(0, elementPosition),
            behavior: 'smooth'
          });
        });
      }
    }
  };

        return (
        <div className="treatment-zone-anchors">
          {zones.map((zone) => {
            const isActive = activeZone === zone.id;
            console.log(`Zone ${zone.id} (${zone.label}): activeZone=${activeZone}, isActive=${isActive}`);
            
            return (
              <button
                key={zone.id}
                onClick={() => handleZoneClick(zone.id)}
                className={cn(
                  "zone-anchor-btn",
                  isActive && "zone-anchor-btn-active"
                )}
                data-zone-id={zone.id}
                data-is-active={isActive}
              >
                {zone.label}
              </button>
            );
          })}
        </div>
      );
};

const TreatmentZones: React.FC = () => {
  const { data: treatmentZones, loading, error } = useTreatmentZones();
  const [activeZone, setActiveZone] = useState<string>('');
  const [headerHeight, setHeaderHeight] = useState<number>(80);
  const locale: 'nl' | 'ar' = typeof window !== 'undefined' ? getCurrentLocale() : 'nl';

  const LOCALE_TEXT: Record<'nl' | 'ar', { buttonText: string; suitableFor: string; noZonesTitle: string; noZonesMessage: string }> = {
    nl: {
      buttonText: 'Over de behandeling',
      suitableFor: 'Geschikt voor:',
      noZonesTitle: 'Geen behandelingen beschikbaar',
      noZonesMessage: 'Er zijn momenteel geen behandelingen beschikbaar.',
    },
    ar: {
      buttonText: 'التعرّف على العلاج',
      suitableFor: 'مناسب لـ:',
      noZonesTitle: 'لا توجد علاجات متاحة',
      noZonesMessage: 'لا توجد علاجات متاحة حاليًا.',
    },
  };

  // Measure header height and set CSS custom property
  useEffect(() => {
    const measureHeaderHeight = () => {
      const headerSelectors = ['header', '.header', '.sticky-header', '[data-sticky="true"]', 'nav'];
      
              for (const selector of headerSelectors) {
          const headerElement = document.querySelector(selector);
          if (headerElement) {
            const height = headerElement.getBoundingClientRect().height;
            setHeaderHeight(height); // Use exact header height
            
            // Set CSS custom property for header height
            document.documentElement.style.setProperty('--header-height', `${height}px`);
            break;
          }
        }
    };

    measureHeaderHeight();
    window.addEventListener('resize', measureHeaderHeight);
    
    return () => window.removeEventListener('resize', measureHeaderHeight);
  }, []);

  // Update active zone based on scroll position and handle sticky class
  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = document.querySelector('header')?.offsetHeight || 80;
      const viewportCenter = window.innerHeight / 2;
      
      // Handle sticky class for anchors
      const anchorsElement = document.querySelector('.treatment-zone-anchors');
      if (anchorsElement) {
        const rect = anchorsElement.getBoundingClientRect();
        const isSticky = rect.top <= headerHeight;
        anchorsElement.classList.toggle('sticky', isSticky);
      }
      
      // Check which zone is in the viewport
      const zoneElements = document.querySelectorAll('[id^="zone-"]');
      let activeZoneInViewport = '';
      
      zoneElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const headerHeight = document.querySelector('header')?.offsetHeight || 80;
        
        // Check if element is in viewport (considering header height)
        // More generous detection for the first zone
        const isInViewport = rect.top <= headerHeight + 200 && rect.bottom >= headerHeight;
        
                                  if (isInViewport) {
           activeZoneInViewport = String(element.id.replace('zone-', ''));
           console.log('Zone in viewport:', activeZoneInViewport, 'type:', typeof activeZoneInViewport);
         }
       });
       
       if (activeZoneInViewport && activeZoneInViewport !== activeZone) {
         console.log('Setting active zone to:', activeZoneInViewport, 'from:', activeZone);
         setActiveZone(activeZoneInViewport);
       }
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  // Function to get appropriate image based on treatment zone title
  const getTreatmentImage = (title: string) => {
    const lowerTitle = title.toLowerCase();
    // Return a placeholder color or gradient for now
    // You can replace these with actual image URLs later
    if (
      lowerTitle.includes('hoofdhuid') ||
      lowerTitle.includes('scalp') ||
      lowerTitle.includes('hair') ||
      lowerTitle.includes('فروة') ||
      lowerTitle.includes('الرأس') ||
      lowerTitle.includes('شعر')
    ) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjZGN0Y5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2Qjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkhhaXIgVHJlYXRtZW50PC90ZXh0Pgo8L3N2Zz4K';
    } else if (
      lowerTitle.includes('baard') ||
      lowerTitle.includes('beard') ||
      lowerTitle.includes('لحية') ||
      lowerTitle.includes('اللحية') ||
      lowerTitle.includes('ذقن')
    ) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjZGN0Y5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2Qjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkJlYXJkIFRyZWF0bWVudDwvdGV4dD4KPC9zdmc+Cg==';
    } else if (
      lowerTitle.includes('wenkbrauw') ||
      lowerTitle.includes('brow') ||
      lowerTitle.includes('حاجب') ||
      lowerTitle.includes('الحواجب')
    ) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjZGN0Y5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2Qjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkJyb3cgVHJlYXRtZW50PC90ZXh0Pgo8L3N2Zz4K';
    } else if (
      lowerTitle.includes('snor') ||
      lowerTitle.includes('mustache') ||
      lowerTitle.includes('شارب')
    ) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjZGN0Y5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2Qjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk11c3RhY2hlIFRyZWF0bWVudDwvdGV4dD4KPC9zdmc+Cg==';
    }
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjZGN0Y5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2Qjc0ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlRyZWF0bWVudDwvdGV4dD4KPC9zdmc+Cg==';
  };

  // Filter out duplicates from Strapi data
  const getUniqueTreatmentZones = (zones: any[] | null) => {
    if (!zones || zones.length === 0) return [];
    
    const seenTitles = new Set();
    const uniqueZones = zones.filter(zone => {
      const title = zone.name;
      if (!title || seenTitles.has(title)) {
        return false;
      }
      seenTitles.add(title);
      return true;
    });
    
    // Sort by order field if available
    return uniqueZones.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Show unique treatment zones (filtered to remove duplicates)
  const allZones = getUniqueTreatmentZones(treatmentZones);

  // Debug logging for treatment zones data
  if (treatmentZones && treatmentZones.length > 0) {
    console.log('🎯 TreatmentZones - ✅ Strapi data loaded:', !!treatmentZones);
    console.log('🎯 TreatmentZones - Total zones:', treatmentZones.length);
    console.log('🎯 TreatmentZones - Unique zones:', allZones.length);
    console.log('🎯 TreatmentZones - First zone structure:', treatmentZones[0]);
    console.log('🎯 TreatmentZones - SuitableFor example:', treatmentZones[0]?.suitableFor);
    console.log('🎯 TreatmentZones - Has image:', !!treatmentZones[0]?.image);
  }

  // Set initial active zone when data loads
  useEffect(() => {
    if (treatmentZones && treatmentZones.length > 0 && !activeZone) {
      const firstZoneId = String(treatmentZones[0].id);
      console.log('Setting initial active zone to:', firstZoneId);
      setActiveZone(firstZoneId);
    }
  }, [treatmentZones, activeZone]);

  if (loading) {
    return (
      <section className="treatment-zones-section">
        <TreatmentZoneAnchors 
          activeZone={activeZone} 
          onZoneChange={setActiveZone}
          headerHeight={headerHeight}
          treatmentZones={treatmentZones}
        />
        <div className="container">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
            <div className="loading-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="loading-card"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="treatment-zones-section">
        <TreatmentZoneAnchors 
          activeZone={activeZone} 
          onZoneChange={setActiveZone}
          headerHeight={headerHeight}
          treatmentZones={treatmentZones}
        />
        <div className="container">
          <div className="error-message">
            <h2>Error loading treatment zones</h2>
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!treatmentZones || treatmentZones.length === 0) {
    return (
      <section className="treatment-zones-section">
        <TreatmentZoneAnchors 
          activeZone={activeZone} 
          onZoneChange={setActiveZone}
          headerHeight={headerHeight}
          treatmentZones={treatmentZones}
        />
        <div className="container">
          <div className="no-data-message">
            <h2>No treatment zones available</h2>
            <p>Please add treatment zones through the CMS.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <TreatmentZoneAnchors 
        activeZone={activeZone} 
        onZoneChange={setActiveZone}
        headerHeight={headerHeight}
        treatmentZones={treatmentZones}
      />
      
      <section className="treatment-zones-section">
        <motion.div
          className="treatment-zones-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
        {allZones.length > 0 ? (
          allZones.map((zone, index) => {
            // Determine zone type based on title
            const zoneType = getZoneType(zone.name);
            
            console.log('Creating grid zone with ID:', `zone-${zone.id}`, 'for zone:', zone.name);
            return (
              <motion.div
                key={zone.id}
                id={`zone-${zone.id}`}
                className="treatment-zone-card"
                variants={itemVariants}
              >
                <div className={cn("zone-content", index % 2 === 1 && "zone-content-reverse")}>
                  <div className="zone-text">
                    <h3 className="zone-title">{zone.name}</h3>
                    <div 
                      className="zone-description"
                      dangerouslySetInnerHTML={{ 
                        __html: zone.description || ''
                      }}
                    />
                    
                    <div className="zone-button">
                      <SlotMachineButton 
                        text={zone.ctaButton?.text || LOCALE_TEXT[locale].buttonText}
                        icon={<ArrowRight />}
                        href="/behandelingen"
                        variant="tertiary"
                        className="treatment-btn"
                      />
                    </div>
                    
                    {zone.suitableFor && (
                      <div className="zone-suitable-for">
                        <div className="suitable-for-header">
                          <div className="check-icon-circle">
                            <span className="check-icon">✓</span>
                          </div>
                          <div>
                            <span className="suitable-for-title">{LOCALE_TEXT[locale].suitableFor}</span>
                            <div className="suitable-for-items">
                              <span className="suitable-for-item">
                                {zone.suitableFor}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="zone-image">
                    {zone.image ? (
                      <img 
                        src={getBestImageUrl(zone.image) || ''} 
                        alt={zone.image?.alternativeText || zone.name || 'Treatment zone'} 
                        className="zone-img"
                      />
                    ) : (
                      <img 
                        src={getTreatmentImage(zone.name || 'default')} 
                        alt={zone.name || 'Treatment zone'} 
                        className="zone-img"
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="no-zones-message">
            <h3>{LOCALE_TEXT[locale].noZonesTitle}</h3>
            <p>{LOCALE_TEXT[locale].noZonesMessage}</p>
          </div>
        )}
        </motion.div>
      </section>
    </>
  );
};

export default TreatmentZones; 